import React, { useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Edit, Folder, Search, Star } from "react-feather";
import {
  Badge,
  Banner,
  Button,
  Checkbox,
  Input,
  SegmentedControl,
  Select,
  stockColorTokens,
  Status,
  Table,
  Tabs,
  Tooltip,
  type TableRow,
  type SelectOption,
} from "@cinesys/ui/components";

/**
 * DEV-ONLY component/token gallery for live-verifying `cinesysExperienceColors.ts` — mounted
 * inside `ExperienceThemeProvider`'s scope (same route group as `home`/`browse`/etc.), so it
 * inherits this app's `cinesysExperienceColors` override by default like every other route there.
 * Never reachable in a prod build (`AppRouter`'s `IS_DEV` gate, same as `DevHome`) even though the
 * page itself ships in the bundle.
 *
 * The palette toggle lives here, not in `ExperienceThemeProvider` — the real experience routes
 * always render the experience palette with no toggle; this page alone can flip back to
 * `@cinesys/ui`'s stock palette for an A/B comparison, via `StockOverride` below re-asserting the
 * stock token values at a higher specificity than `ExperienceThemeProvider`'s own `:root:root`
 * override. It's a real `createGlobalStyle`, mounted/unmounted with this page, so it only ever
 * affects this page and never leaks into the rest of the experience.
 */
const StockOverride = createGlobalStyle`
  :root:root:root {
    ${stockColorTokens}
  }
`;

const Page = styled.div`
  min-height: 100%;
  padding: 32px;
  background: var(--dark060);
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 15px;
  color: var(--gray500);
  border-bottom: 1px solid var(--dark300);
  padding-bottom: 6px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SwatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
`;

const Swatch = styled.div<{ $bg: string }>`
  background: var(${(p) => p.$bg});
  border-radius: 8px;
  padding: 14px 10px;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-size: 11px;
  color: var(--gray500);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const SwatchLabel = styled.code`
  font-size: 10px;
  opacity: 0.85;
`;

const families: { name: string; tokens: string[] }[] = [
  {
    name: "blue (accent)",
    tokens: [
      "--blueBackground",
      "--blueBackgroundSubtle",
      "--blueBorder",
      "--blueBorderOverlay",
      "--blue030",
      "--blue060",
      "--blue090",
      "--blue100",
      "--blue200",
      "--blue300",
      "--blue400",
      "--blue500",
    ],
  },
  {
    name: "green (success)",
    tokens: [
      "--greenBackground",
      "--greenBorder",
      "--greenBorderOverlay",
      "--green100",
      "--green200",
      "--green300",
      "--green400",
      "--green500",
    ],
  },
  {
    name: "orange (warning)",
    tokens: [
      "--orangeBackground",
      "--orangeBorder",
      "--orangeBorderOverlay",
      "--orange100",
      "--orange200",
      "--orange300",
      "--orange400",
      "--orange500",
    ],
  },
  {
    name: "red (danger)",
    tokens: [
      "--redBackground",
      "--redBorder",
      "--redBorderOverlay",
      "--red060",
      "--red100",
      "--red200",
      "--red300",
      "--red400",
      "--red500",
    ],
  },
];

const tableColumns = [
  { title: "Name", width: "1fr" },
  { title: "Status", width: "160px" },
];

const tableRows: TableRow[] = [
  {
    id: "1",
    active: true,
    element: () => <span>Active / selected row</span>,
  },
  {
    id: "2",
    element: () => <span>Ordinary row</span>,
  },
  {
    id: "3",
    element: () => <span>Another ordinary row</span>,
  },
];

const selectOptions: SelectOption[] = [
  { value: "one", label: "Option one" },
  { value: "two", label: "Option two" },
  { value: "three", label: "Option three" },
];

const ColorKitchenSink = () => {
  const [stock, setStock] = useState(false);
  const [checked, setChecked] = useState(true);
  const [segment, setSegment] = useState("b");
  const [selectValue, setSelectValue] = useState<SelectOption | null>(
    selectOptions[0]
  );
  const [inputValue, setInputValue] = useState("Typed text");
  const [tab, setTab] = useState("first");

  return (
    <Page>
      {stock && <StockOverride />}
      <Row>
        <Button
          label={
            stock
              ? "Viewing: stock @cinesys/ui colors"
              : "Viewing: cinesys experience colors"
          }
          icon={Star}
          selected={stock}
          onClick={() => setStock(!stock)}
        />
      </Row>
      <div>
        <SectionTitle>Raw tokens</SectionTitle>
        <Col>
          {families.map((family) => (
            <div key={family.name}>
              <SwatchLabel>{family.name}</SwatchLabel>
              <SwatchGrid style={{ marginTop: 6 }}>
                {family.tokens.map((token) => (
                  <Swatch key={token} $bg={token}>
                    <span>Aa</span>
                    <SwatchLabel>{token}</SwatchLabel>
                  </Swatch>
                ))}
              </SwatchGrid>
            </div>
          ))}
        </Col>
      </div>

      <div>
        <SectionTitle>Buttons</SectionTitle>
        <Row>
          <Button label="Default" type="default" />
          <Button label="Default selected" type="default" selected />
          <Button label="Default disabled" type="default" disabled />
          <Button label="Success" type="success" />
          <Button label="Warning" type="warning" />
          <Button label="Danger" type="danger" />
          <Button icon={Star} label="With icon" type="default" />
          <Button icon={Star} type="default" tooltip="Icon-only button" />
        </Row>
        <Row style={{ marginTop: 10 }}>
          <Button label="Borderless default" type="default" borderless />
          <Button label="Borderless success" type="success" borderless />
          <Button label="Borderless warning" type="warning" borderless />
          <Button label="Borderless danger" type="danger" borderless />
          <Button
            icon={Star}
            type="default"
            borderless
            tooltip="Borderless icon-only"
          />
        </Row>
        <Row style={{ marginTop: 10 }}>
          <Button label="No type" />
          <Button label="No type, selected" selected />
          <Button label="No type, disabled" disabled />
          <Button icon={Edit} label="No type, with icon" />
          <Button icon={Search} tooltip="No type, icon-only" />
          <Button label="No type, borderless" borderless />
          <Button label="No type, borderless selected" borderless selected />
        </Row>
        {/* Passing `selected` (true OR false) — not a separate "pill" prop — is what opts a
            Button into the pill shape/states now (2026-09-09 interactive session: "I don't think
            pills should be a separate state... if we are using selected true or false, it should
            automatically be a pill"), folding in the old "Show Nested"-chip look. Rows below pass
            `selected={false}` explicitly for every "unselected" example so they actually render
            as the pill's unselected state rather than opting out of pill mode entirely (a Button
            that never mentions `selected` stays a normal, non-pill button — see the last row). */}
        <Row style={{ marginTop: 10 }}>
          <Button label="Default (pill)" selected={false} />
          <Button label="Default selected (pill)" selected />
          <Button type="success" label="Success (pill)" selected={false} />
          <Button type="success" label="Success selected (pill)" selected />
          <Button type="warning" label="Warning (pill)" selected={false} />
          <Button type="warning" label="Warning selected (pill)" selected />
          <Button type="danger" label="Danger (pill)" selected={false} />
          <Button type="danger" label="Danger selected (pill)" selected />
        </Row>
        <Row style={{ marginTop: 10 }}>
          <Button icon={Folder} label="With icon (pill)" selected={false} />
          <Button icon={Folder} label="With icon selected (pill)" selected />
          <Button
            icon={Folder}
            label="Disabled (pill)"
            disabled
            selected={false}
          />
          <Button
            icon={Folder}
            label="Disabled selected (pill)"
            disabled
            selected
          />
        </Row>
        {/* Icon-only + `selected` renders as a circle rather than a pill capsule, and a larger
            one (44px) than Button's normal icon-only circle (32px, last example below) — Addison:
            "If it's only an icon, make sure it's a circle, although a larger one." */}
        <Row style={{ marginTop: 10 }}>
          <Button
            icon={Folder}
            tooltip="selected=false — pill circle, unselected, 44px"
            selected={false}
          />
          <Button
            icon={Folder}
            tooltip="selected=true — pill circle, selected (check icon), 44px"
            selected
          />
          <Button
            icon={Folder}
            tooltip="No selected prop — plain icon button, 32px"
          />
        </Row>
      </div>

      <div>
        <SectionTitle>Checkbox</SectionTitle>
        <SwatchLabel>
          Three visual shapes share this component: box (`showBox`), radio
          (`showRadio`), and the default slider/toggle (neither prop set).
        </SwatchLabel>
        <Row style={{ marginTop: 10 }}>
          <Checkbox
            label="Box, unchecked"
            value={false}
            showBox
            onChange={() => {}}
          />
          <Checkbox label="Box, checked" value showBox onChange={() => {}} />
          <Checkbox
            label="Box, indeterminate"
            value={false}
            indeterminate
            showBox
            onChange={() => {}}
          />
          <Checkbox
            label="Radio, unselected"
            value={false}
            showRadio
            onChange={() => {}}
          />
          <Checkbox
            label="Radio, selected"
            value
            showRadio
            onChange={() => {}}
          />
          <Checkbox
            label="Slider (default), off"
            value={false}
            onChange={() => {}}
          />
          <Checkbox
            label="Slider (default), on"
            value={checked}
            onChange={setChecked}
          />
        </Row>
        <Row style={{ marginTop: 10 }}>
          <Checkbox
            label="Unchecked"
            value={false}
            showBox
            onChange={() => {}}
          />
          <Checkbox
            label="Checked"
            value={checked}
            showBox
            onChange={setChecked}
          />
          <Checkbox
            label="Indeterminate"
            value={false}
            indeterminate
            showBox
            onChange={() => {}}
          />
          <Checkbox
            label="Disabled unchecked"
            value={false}
            showBox
            disabled
            onChange={() => {}}
          />
          <Checkbox
            label="Disabled checked"
            value
            showBox
            disabled
            onChange={() => {}}
          />
        </Row>
        <Row style={{ marginTop: 10 }}>
          <Checkbox label="Dense" value showBox dense onChange={() => {}} />
          <Checkbox
            label="Custom box border"
            value={false}
            showBox
            boxBorderColor="var(--red400)"
            onChange={() => {}}
          />
        </Row>
        <Row style={{ marginTop: 10 }}>
          <Checkbox
            label="With description"
            description="Extra explanatory text underneath the label"
            value={false}
            showBox
            onChange={() => {}}
          />
          <Checkbox
            label="With tooltip"
            tooltip="Shown on hover"
            value={false}
            showBox
            onChange={() => {}}
          />
          <Checkbox
            label="Inline label"
            inlineLabel
            value={false}
            showBox
            onChange={() => {}}
          />
          <Checkbox
            label="Left label"
            leftLabel
            value={false}
            showBox
            onChange={() => {}}
          />
        </Row>
        <Row
          style={{ marginTop: 10, background: "var(--dark200)", padding: 12 }}
        >
          <Checkbox
            label="Overlay style (padBox)"
            value={false}
            showBox
            padBox
            onChange={() => {}}
          />
          <Checkbox
            label="Overlay style, idle background (padBoxBackground)"
            value={false}
            showBox
            padBox
            padBoxBackground
            onChange={() => {}}
          />
        </Row>
      </div>

      <div>
        <SectionTitle>Segmented control</SectionTitle>
        <Row>
          <SegmentedControl
            value={segment}
            onChange={setSegment}
            options={[
              { value: "a", label: "One" },
              { value: "b", label: "Two" },
              { value: "c", label: "Three" },
            ]}
          />
        </Row>
      </div>

      <div>
        <SectionTitle>Select &amp; Input</SectionTitle>
        <Row>
          <div style={{ width: 220 }}>
            <Select
              value={selectValue}
              options={selectOptions}
              onChange={(v) => setSelectValue(v as SelectOption)}
            />
          </div>
          <div style={{ width: 220 }}>
            <Input
              label="Text input"
              value={inputValue}
              onChange={setInputValue}
              clearable
            />
          </div>
          <div style={{ width: 220 }}>
            <Input
              label="With submit action"
              value=""
              onChange={() => {}}
              placeholder="Search…"
              submitIcon={Search}
              onSubmit={() => {}}
            />
          </div>
        </Row>
      </div>

      <div>
        <SectionTitle>Tooltips (forced visible)</SectionTitle>
        {/* Tooltip clones its child and attaches a ref to it — that only works on a real DOM
            element (a native `<span>`/`<div>`, or a component built with `React.forwardRef`).
            `Button` is a plain function component with no forwardRef, so `<Tooltip><Button/>
            </Tooltip>` silently never shows (the ref never attaches, `mounted` stays false) —
            wrap a plain `<span>` around the Button instead, or use Button's own built-in
            `tooltip` prop (it wraps itself in a Tooltip internally) as shown in the last one. */}
        <Row>
          <Tooltip label="Default tooltip" visible>
            <span>
              <Button label="Hover target" />
            </span>
          </Tooltip>
          <Tooltip label="Danger tooltip" type="danger" visible>
            <span>
              <Button label="Danger" type="danger" />
            </span>
          </Tooltip>
          <Tooltip label="Success tooltip" type="success" visible>
            <span>
              <Button label="Success" type="success" />
            </span>
          </Tooltip>
          <Tooltip label="Warning tooltip" type="warning" visible>
            <span>
              <Button label="Warning" type="warning" />
            </span>
          </Tooltip>
          <Button
            label="Built-in tooltip prop"
            tooltip="Shown via Button's own tooltip prop"
          />
        </Row>
      </div>

      <div>
        <SectionTitle>Banners</SectionTitle>
        <Col>
          <Banner error="Something went wrong" />
          <Banner warn="Heads up, check this" />
          <Banner success="All good" />
        </Col>
      </div>

      <div>
        <SectionTitle>Badge &amp; Status</SectionTitle>
        <Row>
          <Badge color="var(--blue400)" label="Accent badge" />
          <Badge color="var(--green400)" label="Success badge" />
          <Badge color="var(--orange400)" label="Warning badge" />
          <Badge color="var(--red400)" label="Danger badge" />
        </Row>
        <Row style={{ marginTop: 10 }}>
          <Status type="error">
            <Edit size={14} /> Error status
          </Status>
          <Status type="warn">
            <Edit size={14} /> Warn status
          </Status>
          <Status type="success">
            <Edit size={14} /> Success status
          </Status>
        </Row>
      </div>

      <div>
        <SectionTitle>Tabs</SectionTitle>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { key: "first", label: "First tab" },
            { key: "second", label: "Second tab" },
            { key: "third", label: "Third tab" },
          ]}
        />
      </div>

      <div>
        <SectionTitle>Table (one active row)</SectionTitle>
        <div style={{ height: 180 }}>
          <Table columns={tableColumns} rows={tableRows} height="180px" />
        </div>
      </div>
    </Page>
  );
};

export default ColorKitchenSink;
